/* ===================================================================
   useTeam() + useInviteTeamMember() -- team invitations (Brad, 2026-06-17).

   Read:   GET  /api/v1/team
   Invite: POST /api/v1/team  (records the invite; NO email is sent here;
           idempotent on (client_id, email))

   The Settings -> Team panel binds to these. After an invite we invalidate
   so the member list refetches. Read hook disabled until a Bearer rr_ token
   exists.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type TeamInviteResult,
  type TeamResponse,
  type TeamRole,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useTeam(): UseQueryResult<TeamResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.team(),
    queryFn: ({ signal }) => api.listTeam(signal),
    enabled: hasToken,
    staleTime: 30_000,
  });
}

export interface UseInviteTeamMemberResult {
  invite: (input: { email: string; role?: TeamRole }) => Promise<TeamInviteResult>;
  isPending: boolean;
  error: Error | null;
}

export function useInviteTeamMember(): UseInviteTeamMemberResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: { email: string; role?: TeamRole }) =>
      api.inviteTeamMember(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.team() });
    },
  });
  return {
    invite: (input) => m.mutateAsync(input),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}
